/**
 * URL slug for a public user profile. The first 8 chars of the UUID are
 * appended to the name (same approach as the realEstate slug). Short, readable,
 * and unique enough — collisions on 8 random hex chars are extremely unlikely
 * with realistic user counts.
 *
 * Example: { id: "f559d9f7-a77b-4354-80e1-19f3506d8c27", name: "Pedro Rego" }
 *   -> "pedro-rego-f559d9f7"
 */
export function buildUserProfileSlug(args: {
  id: string;
  name?: string | null;
}): string {
  const namePart =
    (args.name ?? "")
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "user";
  const idPart = args.id.split("-")[0];
  return `${namePart}-${idPart}`;
}

/**
 * Pulls the short id (8 hex chars) back out of a slug produced by
 * buildUserProfileSlug. Returns null if the slug doesn't end with a valid
 * 8-char hex token.
 */
export function extractUserShortIdFromSlug(slug: string): string | null {
  if (!slug) return null;
  const last = slug.split("-").pop();
  if (!last || !/^[0-9a-f]{8}$/i.test(last)) return null;
  return last.toLowerCase();
}
